<template>
  <div class="owv" data-testid="onboarding-wizard-view">
    <div v-if="isLoading" class="owv-loading" data-testid="owv-loading">
      <span class="owv-spinner" aria-hidden="true"></span>
      <span class="owv-sr-only">Loading your welcome wizard</span>
    </div>

    <template v-else-if="pages.length">
      <p v-if="actionError" class="owv-error" role="alert" data-testid="owv-error">
        {{ actionError }}
      </p>

      <OnboardingWizard
        :pages="pages"
        :current-index="currentIndex"
        :landing-path="landingPath"
        :show-back="showBack"
        :user-first-name="userFirstName"
        :user-last-name="userLastName"
        :user-email="userEmail"
        :user-picture="userPicture"
        @back="onBack"
        @continue="onContinue"
        @skip="onSkip"
        @do-not-ask="onDoNotAsk"
        @replace-photo="onReplacePhoto"
        @remove-photo="onRemovePhoto"
      />
    </template>
  </div>
</template>

<script setup>
/**
 * ER-12245 — OnboardingWizardView (MDP-621).
 *
 * The flow controller the previous implementation was missing. It reads the
 * wizard state from ER-12241, hands the page list to the presentational
 * `OnboardingWizard` and owns the one thing that component deliberately does
 * not: advancing, persisting and landing.
 *
 * Rules it implements (Confluence "Invite and Onboarding Wizards — Rules"):
 * 3.5 one stepper segment per step, 3.6 open at the first unfinished step and
 * replace the landing page until finished, 6.2 Continue completes while Skip
 * keeps the page pending and Do not ask again hides it for good, 6.3 end when
 * no page is left.
 */
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import { useRouter } from "vue-router/composables";
import OnboardingWizard from "@components/OnboardingWizard/OnboardingWizard.vue";
import { uploadAccountLogo } from "@/api/accountLogoAPI";
import { updateDirectory } from "@/api/appsAPI";
import { getAccountData } from "@/api/employeeAPI";
import {
  completeWelcomeWizardPage,
  dismissWelcomeWizardPage,
  getWelcomeWizardProfile,
  getWelcomeWizardState,
  saveWelcomeWizardProfile,
  skipWelcomeWizardPage,
} from "@/api/userAPI";
import {
  buildWizardPages,
  clearWizardBypass,
  initialStepIndex,
  isFlowRenderable,
  isOpenStep,
  isStepRenderable,
  nextRenderableOpenIndex,
  previousOpenIndex,
  readWelcomeWizardIds,
  setWizardBypass,
  toRouterLocation,
} from "@/utils/welcomeWizard";

const instance = getCurrentInstance();
const router = instance?.proxy?.$router || useRouter();

const isLoading = ref(true);
const actionError = ref("");
const isBusy = ref(false);

const steps = ref([]);
const flow = ref("");
const currentIndex = ref(0);
const landingPath = ref("/");
const profile = ref(null);
const accountName = ref("");
const logoUrl = ref("");
const userFirstName = ref("");
const userLastName = ref("");
const userEmail = ref("");
const userPicture = ref("");
const companyWebsites = ref([]);

/** The uploaded picture file, held until the step is saved (rule 5.4). */
const photoFile = ref(null);

const accountId = ref(null);
const accessId = ref(null);

const pages = computed(() =>
  buildWizardPages(steps.value, {
    flow: flow.value,
    accountName: accountName.value,
    logoUrl: logoUrl.value,
    profile: profile.value,
    firstName: userFirstName.value,
    websites: companyWebsites.value,
  })
);

/**
 * Back never lands on a completed page: Welcome and Earn your readiness badges
 * are shown once (rule 6.2), so a page behind the user can be a filled segment
 * without being reachable.
 */
const showBack = computed(
  () => previousOpenIndex(steps.value, currentIndex.value, flow.value) !== -1
);

function readStoredJson(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}") || {};
  } catch (error) {
    return {};
  }
}

function loadIdentity() {
  const user = readStoredJson("user");
  const accountDetails = readStoredJson("currentAccountDetails");
  const accessPermission = readStoredJson("accessPermission");

  userFirstName.value = user.first_name || "";
  userLastName.value = user.last_name || "";
  userEmail.value = user.email || user.username || "";
  userPicture.value = user.picture || "";
  accountName.value = accountDetails.name || accessPermission.account_name || "";
  logoUrl.value = accountDetails.logo_url || "";
}

/**
 * Leave the wizard for the flow's landing page (rule 6.3).
 *
 * A beforeEach guard can redirect this navigation — handleDeepLinkAccount
 * rewrites the landing path and drops the `accountId` query — and Vue Router
 * rejects the returned promise for a redirect. The redirect itself completes,
 * so the rejection must be swallowed; leaving it unhandled showed
 * "Uncaught runtime errors: Redirected when going from /welcome-wizard to
 * /organization/dashboard" every time a wizard finished (QA 2026-10-29).
 */
function leaveWizard() {
  return router.replace(landingPath.value || "/").catch(() => {});
}

/**
 * Fail open: the wizard must never trap the user or block the app. Mark the
 * account so the entry gate does not send them straight back here, then leave.
 */
function failOpen() {
  setWizardBypass(accountId.value);
  leaveWizard();
}

/** The state says this Access has no wizard to show after all. */
function notApplicable() {
  leaveWizard();
}

async function loadProfileIfNeeded() {
  const hasProfileStep = steps.value.some((step) => step.key === "profile");
  if (!hasProfileStep) return;

  try {
    const response = await getWelcomeWizardProfile(accountId.value, accessId.value);
    profile.value = response?.data ?? null;
  } catch (error) {
    // The step still renders from the invite names; the photo is optional.
    profile.value = null;
  }
}

async function loadWebsitesIfNeeded() {
  const hasWebsiteStep = steps.value.some((step) => step.key === "website");
  if (!hasWebsiteStep) return;

  try {
    const response = await getAccountData(accountId.value, { fresh: true });
    const saved = response?.data?.company_websites;
    companyWebsites.value = Array.isArray(saved) ? saved : [];
  } catch (error) {
    // The page still works from an empty list; saving replaces what is stored, so a
    // failed read must not stop the user from skipping.
    companyWebsites.value = [];
  }
}

async function load() {
  const ids = readWelcomeWizardIds();
  accountId.value = ids.accountId;
  accessId.value = ids.accessId;

  if (!accountId.value || !accessId.value) {
    notApplicable();
    return;
  }

  loadIdentity();

  try {
    const response = await getWelcomeWizardState(accountId.value, accessId.value);
    const state = response?.data ?? null;

    steps.value = Array.isArray(state?.steps) ? state.steps : [];
    flow.value = state?.flow || "";
    landingPath.value = toRouterLocation(state?.landing_path);

    // Rule 6.3: nothing pending — the wizard is over.
    if (!steps.value.length || !state?.current_step) {
      notApplicable();
      return;
    }
    // The administrator Welcome wording is not supplied yet; opening on a page
    // this build cannot draw would leave the user on a blank step, so keep the
    // normal landing. Capability-based, so supplying that copy lights the
    // administrator flows up without touching the gate.
    if (
      !isFlowRenderable(flow.value, steps.value) ||
      !isStepRenderable(state.current_step, flow.value)
    ) {
      notApplicable();
      return;
    }

    await loadProfileIfNeeded();
    await loadWebsitesIfNeeded();

    currentIndex.value = initialStepIndex(steps.value, state.current_step);
    clearWizardBypass(accountId.value);
    isLoading.value = false;
  } catch (error) {
    failOpen();
  }
}

/** Keep the local copy of the list in step with what we just told the server. */
function markStepDone(index, status) {
  const step = steps.value[index];
  if (step) step.status = status;
}

/**
 * The wizard is over for this session.
 *
 * When pages are still open (the user skipped the last one, which stays pending
 * by rule 6.2) mark the account so the entry gate does not reopen the wizard in
 * a loop. The marker is per session and per account, so the skipped page is
 * offered again at the next sign-in, as the rule requires.
 */
function finishSession() {
  if (steps.value.some((step) => isOpenStep(step))) {
    setWizardBypass(accountId.value);
  }
  leaveWizard();
}

function advanceAfter(index) {
  const next = nextRenderableOpenIndex(steps.value, index, flow.value);
  if (next === -1) {
    finishSession();
    return;
  }
  currentIndex.value = next;
}

async function runAction(status, request) {
  if (isBusy.value) return;

  const index = currentIndex.value;
  const step = steps.value[index];
  if (!step) return;

  isBusy.value = true;
  actionError.value = "";

  try {
    await request(step);
    markStepDone(index, status);
    advanceAfter(index);
  } catch (error) {
    actionError.value =
      "We couldn't save your progress. Please check your connection and try again.";
  } finally {
    isBusy.value = false;
  }
}

function buildProfileFormData(payload) {
  const formData = new FormData();
  if (payload?.firstName) formData.append("first_name", payload.firstName);
  if (payload?.lastName) formData.append("last_name", payload.lastName);
  if (photoFile.value) formData.append("picture", photoFile.value);
  return formData;
}

async function onContinue(event) {
  const payload = event?.payload;

  await runAction("completed", async (step) => {
    if (step.key === "profile") {
      await saveWelcomeWizardProfile(
        accountId.value,
        accessId.value,
        buildProfileFormData(payload)
      );
    }
    // ER-12246 / ER-12244: the logo and the websites are saved before the page completes,
    // so a failed save leaves the page open instead of marking it done.
    if (step.key === "branding" && payload?.file) {
      await uploadAccountLogo(accountId.value, payload.file);
    }
    if (step.key === "website" && payload?.websites?.length) {
      await updateDirectory(accountId.value, { company_websites: payload.websites });
    }
    await completeWelcomeWizardPage(accountId.value, accessId.value, step.key);
  });
}

async function onSkip() {
  await runAction("skipped", (step) =>
    skipWelcomeWizardPage(accountId.value, accessId.value, step.key)
  );
}

async function onDoNotAsk() {
  await runAction("dismissed", (step) =>
    dismissWelcomeWizardPage(accountId.value, accessId.value, step.key)
  );
}

function onBack() {
  const previous = previousOpenIndex(steps.value, currentIndex.value, flow.value);
  if (previous !== -1) {
    currentIndex.value = previous;
  }
}

function onReplacePhoto(event) {
  if (event?.payload) {
    photoFile.value = event.payload;
  }
}

/**
 * "Remove" clears a file the user picked in this session. The write contract
 * (ER-12243) has no delete: a picture that is already stored stays, which is
 * why this never clears `profile.picture`.
 */
function onRemovePhoto() {
  photoFile.value = null;
}

onMounted(load);
</script>

<style scoped>
.owv {
  box-sizing: border-box;
  min-height: 100vh;
  background: #ffffff;
  font-family: Inter, sans-serif;
}

.owv-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
}

.owv-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #d0d5dd;
  border-top-color: #7f56d9;
  border-radius: 50%;
  animation: owv-spin 0.8s linear infinite;
}

.owv-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.owv-error {
  margin: 0;
  padding: 12px 16px;
  background: #fef3f2;
  border-bottom: 1px solid #fecdca;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
}

@keyframes owv-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
